import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { logout } from './authActions';
import { toast } from 'react-toastify';
import { translate } from 'controller/constants';

export const fetchData = createAsyncThunk(
    'data/fetchData',
    async ({ endpoint, params, dataKey }, thunkAPI) => {
        const { getState, rejectWithValue, dispatch } = thunkAPI;
        try {
            const { auth } = getState();
            const queryString = new URLSearchParams(params).toString();
            const url = `${endpoint}?${queryString}`;
            const response = await axios.get(url, {
                headers: {
                    'Authorization': `Bearer ${auth.access_token}`,
                    'accept': 'application/json'
                }
            });
            return { dataKey, data: response.data };
        } catch (error) {
            if (error.response?.status === 401) {
                dispatch(logout());
                return rejectWithValue('Unauthorized');
            } else {
                return rejectWithValue(error.response?.status || "Error fetching data");
            }
        }
    }
);

export const deleteData = createAsyncThunk(
    'data/deleteData',
    async ({ endpoint, identifiers, data = {} }, thunkAPI) => {
        const { getState, rejectWithValue, dispatch } = thunkAPI;
        const { auth } = getState();

        try {
            const promises = identifiers.map(async (identifier) => {
                const url = `${endpoint.replace(/\/$/, '')}/${identifier}`;
                await axios.delete(url, {
                    headers: {
                        'Authorization': `Bearer ${auth.access_token}`,
                        'accept': 'application/json',
                        'Content-Type': 'application/json',
                    },
                    data,
                });
            });
            await Promise.all(promises);
            return { identifiers };
        } catch (error) {
            // Extract error data from response
            const errorData = error.response?.data;

            // Handle the error message display
            if (errorData) {
                console.log('errorData', errorData);
                toast.error('Erro ao apagar item: ' + translate(errorData));
                console.log('errorData translated', translate(errorData));
            } else {
                toast.error('Erro ao apagar item: Ocorreu um erro desconhecido');
            }

            // Handle authentication error
            if (error.response?.status === 401) {
                dispatch(logout());
                return rejectWithValue('Unauthorized');
            } else {
                return rejectWithValue(error.response?.status || "Error deleting data");
            }
        }
    }
);

export const postData = createAsyncThunk(
    'data/postData',
    async ({ endpoint, data, dataKey }, thunkAPI) => {
        const { getState, rejectWithValue, dispatch } = thunkAPI;
        try {
            const { auth } = getState();
            const isMultipart = data instanceof FormData || (typeof data === 'object' && 'file' in data);
            const headers = {
                'Authorization': `Bearer ${auth.access_token}`,
                'Content-Type': isMultipart ? 'multipart/form-data' : 'application/json',
                'accept': 'application/json',
            };

            const response = await axios.post(endpoint, data, { headers });
            console.log('response', response);
            toast.success('Dados submetidos com sucesso');

            return { dataKey, data: response.data };
        } catch (error) {
            console.error('Error posting data:', error);

            // Display an error message using toast
            toast.error('Erro ao enviar: ' + translate(error.response?.data));

            // Handle 401 Unauthorized by dispatching a logout action
            if (error.response?.status === 401) {
                dispatch(logout());
                return rejectWithValue('Unauthorized');
            } else {
                // Return the error message or a generic error message
                return rejectWithValue(error.response?.status || "Error posting data");
            }
        }
    }
);

export const patchData = createAsyncThunk(
    'data/patchData',
    async ({ endpoint, data, dataKey }, thunkAPI) => {
        const { getState, rejectWithValue, dispatch } = thunkAPI;
        try {
            const { auth } = getState();
            const isMultipart = data instanceof FormData || (typeof data === 'object' && 'file' in data);

            const headers = {
                'Authorization': `Bearer ${auth.access_token}`,
                'Content-Type': isMultipart ? 'multipart/form-data' : 'application/json',
                'accept': 'application/json',
            };

            // Axios PATCH request
            const response = await axios.patch(endpoint, data, { headers });

            toast.success('Dados atualizados com sucesso');
            return { dataKey, data: response.data };
        } catch (error) {
            // Display an error message using toast
            toast.error('Erro ao atualizar dados: ' + translate(error.response?.data));

            // Handle 401 Unauthorized by dispatching a logout action
            if (error.response?.status === 401) {
                dispatch(logout());
                return rejectWithValue('Unauthorized');
            } else {
                // Return the error message or a generic error message
                return rejectWithValue(error.response?.status || "Error updating data");
            }
        }
    }
);


export const putData = createAsyncThunk(
    'data/putData',
    async ({ endpoint, data, dataKey }, thunkAPI) => {
        const { getState, rejectWithValue, dispatch } = thunkAPI;
        try {
            const { auth } = getState();
            const isMultipart = data instanceof FormData || (typeof data === 'object' && 'file' in data);

            const headers = {
                'Authorization': `Bearer ${auth.access_token}`,
                'Content-Type': isMultipart ? 'multipart/form-data' : 'application/json',
                'accept': 'application/json',
            };

            // Axios PUT request
            const response = await axios.put(endpoint, data, { headers });

            toast.success('Dados atualizados com sucesso');
            return { dataKey, data: response.data };
        } catch (error) {
            //console.error('Error updating data:', error);

            // Display an error message using toast
            toast.error('Erro ao atualizar dados: ' + translate(error.response?.data));

            // Handle 401 Unauthorized by dispatching a logout action
            if (error.response?.status === 401) {
                dispatch(logout());
                return rejectWithValue('Unauthorized');
            } else {
                // Return the error message or a generic error message
                return rejectWithValue(error.response?.status || "Error updating data");
            }
        }
    }
);

// New clearData action to clear a specific dataKey
export const clearData = createAction('data/clearData', (dataKey) => {
    return {
        payload: {
            dataKey
        }
    };
});
